'use client';

import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { GET_LEASING_RIGHT } from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { LeasingRight } from '../../../types/leases';
import { dayjs } from '../../../utils';
import { isTenantPortal } from '../../../utils/constants';
import { Snackbar } from '../../alerts';
import { Dialog } from '../../modals';
import { TabPanel } from '../../tabPanel';
import { LeaseUtilization } from '../leaseUtilizationPage/leaseUtilization';
import { CreateLeaseForm } from './createLeaseForm';
import { LeaseCountAndRates } from './leaseCountAndRates';
import { LeaseGeneral } from './leaseGeneral';
import { Subtenants } from './subtenants';

type LeaseDetailPageProps = {
	setShowDialog?: SetShowDialog;
	setShowSnackbar?: SetShowSnackbar;
	getUrlToSubscriptionPage?: (...x: any[]) => string;
	setSubscriptionId?: Dispatch<SetStateAction<string>>;
	initialData?: any;
	leasingRightId?: number;
	urlIsLoading?: boolean;
};

const emptyLeasingRight = {
	commencementDate: dayjs().format('YYYY-MM-DD'),
	expirationDate: dayjs().format('YYYY-MM-DD'),
	buildings: [],
	countAndRates: [],
	subtenants: [],
} as LeasingRight;

export const LeaseDetailPage: FC<LeaseDetailPageProps> = ({
	setShowDialog = null,
	setShowSnackbar = null,
	getUrlToSubscriptionPage,
	setSubscriptionId,
	initialData,
	leasingRightId,
	urlIsLoading,
}) => {
	const [tab, setTab] = useState(0);
	const [leasingRight, setLeasingRight] = useState<LeasingRight>(initialData?.getLeasingRight || emptyLeasingRight);
	const [uneditedLeasingRight, setUneditedLeasingRight] = useState<LeasingRight>(null);
	const [showSnackbarForLeaseDetail, setShowSnackbarForLeaseDetail = setShowSnackbar] = useState(null);
	const [showDialogForLeaseDetail, setShowDialogForLeaseDetail = setShowDialog] = useState(null);
	const newMode = useMemo(() => !leasingRightId, [leasingRightId]);

	const [isLeaseGeneralEditing, setIsLeaseGeneralEditing] = useState(newMode);
	const [isCountAndRatesEditing, setIsCountAndRatesEditing] = useState(false);

	const isLeaseDetailEditInProgress = useMemo(
		() => isLeaseGeneralEditing || isCountAndRatesEditing,
		[isCountAndRatesEditing, isLeaseGeneralEditing],
	);

	const {
		data,
		loading: leasingRightLoading,
		refetch: refetchLeasingRight,
	} = useQuery(GET_LEASING_RIGHT, {
		variables: { leasingRightId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !leasingRightId || initialData,
		notifyOnNetworkStatusChange: true,
	});

	const leasingRightData = data || initialData;

	useEffect(() => {
		if (!leasingRightData?.getLeasingRight) return;

		setLeasingRight(leasingRightData.getLeasingRight);
		setUneditedLeasingRight(leasingRightData.getLeasingRight);
		if (setSubscriptionId) setSubscriptionId(leasingRightData.getLeasingRight.subscriptionId);
	}, [leasingRightData, setSubscriptionId]);

	const resetLeasingRight = useCallback(() => setLeasingRight(uneditedLeasingRight), [uneditedLeasingRight]);

	const loading = urlIsLoading || leasingRightLoading;
	if (loading) {
		return (
			<Box display='flex' justifyContent='center' marginTop='8em'>
				<CircularProgress size='4em' />
			</Box>
		);
	}

	if (!urlIsLoading && newMode) {
		if (isTenantPortal) {
			window.location.href = '/leases';
			return null;
		}

		return (
			<CreateLeaseForm
				leasingRight={leasingRight}
				setLeasingRight={setLeasingRight}
				setShowDialog={setShowDialog}
				setShowSnackbar={setShowSnackbar}
				getUrlToSubscriptionPage={getUrlToSubscriptionPage}
			/>
		);
	}

	return (
		<>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs
					value={tab}
					onChange={(_, newTab) => setTab(newTab)}
					aria-label='tabs'
					data-cy='lease-detail-tabs'
				>
					<Tab label='Details' data-cy='details-tab' />
					<Tab label='Utilization' />
				</Tabs>
			</Box>
			<TabPanel value={tab} index={0}>
				<LeaseGeneral
					leasingRight={leasingRight}
					setLeasingRight={setLeasingRight}
					refetchLeasingRight={refetchLeasingRight}
					resetLeasingRight={resetLeasingRight}
					setShowDialog={setShowDialogForLeaseDetail}
					setShowSnackbar={setShowSnackbarForLeaseDetail}
					isLeaseGeneralEditing={isLeaseGeneralEditing}
					setIsLeaseGeneralEditing={setIsLeaseGeneralEditing}
					isLeaseDetailEditInProgress={isLeaseDetailEditInProgress}
					newMode={newMode}
				/>
				<Subtenants
					subtenants={leasingRight.subtenants}
					leasingRight={leasingRight}
					refetchLeasingRight={refetchLeasingRight}
					setShowDialog={setShowDialogForLeaseDetail}
					setShowSnackbar={setShowSnackbarForLeaseDetail}
					getUrlToSubscriptionPage={getUrlToSubscriptionPage}
				/>
				<LeaseCountAndRates
					leasingRight={leasingRight}
					refetchLeasingRight={refetchLeasingRight}
					setShowDialog={setShowDialogForLeaseDetail}
					setShowSnackbar={setShowSnackbarForLeaseDetail}
					setIsCountAndRatesEditing={setIsCountAndRatesEditing}
					isLeaseDetailEditInProgress={isLeaseDetailEditInProgress}
				/>
			</TabPanel>
			<TabPanel value={tab} index={1}>
				<LeaseUtilization
					leasingRight={leasingRight}
					setShowDialog={setShowDialogForLeaseDetail}
					getUrlToSubscriptionPage={getUrlToSubscriptionPage}
				/>
			</TabPanel>
			<Snackbar showSnackbar={showSnackbarForLeaseDetail} setShowSnackbar={setShowSnackbarForLeaseDetail} />
			<Dialog showDialog={showDialogForLeaseDetail} setShowDialog={setShowDialogForLeaseDetail} />
		</>
	);
};
